import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { useTranslations, useLanguage } from "@vocab/react";
import { TooltipRenderer, Box, Text, IconCritical, IconTick, IconRecommended, FieldMessage, MenuRenderer, MenuItem, IconMail, IconSocialFacebook, IconSocialX, IconSocialLinkedIn, MenuItemDivider, IconFlag, ButtonIcon, IconOverflow, Columns, Column, Link, IconNewWindow, HiddenVisually, Stack, TextLink, Notice, Hidden, Disclosure, TextField, Dropdown, Textarea, Actions, Button, IconBookmark, useResponsiveValue, Inline, IconInfo, Secondary, IconThumb, useToast, TextLinkButton, IconChevron, Heading, ButtonLink, Alert, Tiles, List, Divider } from "braid-design-system";
import { breakpoints } from "braid-design-system/css";
import { useInView } from "react-intersection-observer";
import { useJobDetailsViewContext, useAnalyticsHandlers, useQualities, JobDetailsViewContextProvider, CompanyProfile } from "../CompanyProfile/CompanyProfile.mjs";
import { useShouldReduceDuplicateApply, useIsApplyDisabled, JDVLayout } from "../JDVLayout/JDVLayout.mjs";
import { translations as translations$1 } from "../../../components/.vocab/index.mjs";
import { useCallback, useEffect, useState, useMemo, useRef, createContext, useContext } from "react";
import { translations } from "../../../components/JobStatusBadge/.vocab/index.mjs";
import { ApplyButton } from "../ApplySection/ApplyButton/ApplyButton.mjs";
import { CompetitiveJobs } from "../CompetitiveJobs/CompetitiveJobs.mjs";
import { UnsafeJobHtmlRenderer } from "@seek/job-html-renderer";
import compact from "lodash/compact.js";
import escapeRegExp from "lodash/escapeRegExp.js";
import join from "lodash/join.js";
import replace from "lodash/replace.js";
import { JobDetailsViewHeader } from "../JobDetailsViewHeader/JobDetailsViewHeader.mjs";
import { JobDetailsViewHeaderAdditional } from "../JobDetailsViewHeaderAdditional/JobDetailsViewHeaderAdditional.mjs";
import { useJobUrl, jobUrlOriginValues } from "../CompetitiveJobs/JobCardLite/JobCardLite.mjs";
import { translations as translations$2 } from "../../../components/OptionsDropdownMenu/.vocab/index.mjs";
import { translations as translations$3 } from "../../../components/OptionsBar/.vocab/index.mjs";
import { createForm, useField } from "@seek/forms-ui";
import { useMelwaysLink, useMelwaysInfo } from "@seek/melways-react";
import { isEmail, maxLength } from "@seek/validators-js";
import { ResponsiveEmbed } from "../ResponsiveEmbed/ResponsiveEmbed.mjs";
import { SkeletonLine } from "../CompetitiveJobs/CompetitiveJobsSkeleton.mjs";
import { translations as translations$6 } from "../../../components/SkillsMatch/SkillsMatchSignedIn/.vocab/index.mjs";
import { translations as translations$4 } from "../../../components/SkillsMatch/SkillsMatchSignedIn/ErrorMessage/.vocab/index.mjs";
import { translations as translations$5 } from "../../../components/SkillsMatch/AlertMessages/.vocab/index.mjs";
import { translations as translations$7 } from "../../../components/SkillsMatch/SkillsMatchSignedIn/FeedbackIndicator/.vocab/index.mjs";
import { translations as translations$9 } from "../../../components/SkillsMatch/.vocab/index.mjs";
import { QualityTag } from "../SkillsMatch/SkillsMatchSignedIn/SelectableQualities/QualityTag.mjs";
import { translations as translations$8 } from "../../../components/SkillsMatch/SkillsMatchSignedIn/SelectableQualities/.vocab/index.mjs";
import { translations as translations$a } from "../../../components/SkillsMatch/SkillsMatchSignedIn/ExpandedContent/.vocab/index.mjs";
import { translations as translations$b } from "../../../components/SkillsMatch/SkillsMatchSignedIn/ShowMore/.vocab/index.mjs";
import { UnselectableQualities } from "../SkillsMatch/SkillsMatchSignedIn/UnselectableQualities/UnselectableQualities.mjs";
import { translations as translations$c } from "../../../components/SkillsMatch/SkillsMatchSignedIn/QualitiesSection/.vocab/index.mjs";
import { translations as translations$d } from "../../../components/SkillsMatch/SkillsMatchSignedIn/Tooltip/.vocab/index.mjs";
import { sourcrImage } from "./JobDetailsView.css.mjs";
const iconForTone = {
  promote: IconRecommended,
  positive: IconTick,
  critical: IconCritical
};
const backgroundForTone = {
  promote: "promoteLight",
  positive: "positiveLight",
  critical: "criticalLight"
};
const JobStatusBadgeBaseView = ({
  background,
  label,
  id,
  Icon,
  data
}) => /* @__PURE__ */ jsx(
  Box,
  {
    display: "inlineBlock",
    borderRadius: "standard",
    background,
    paddingY: "xxsmall",
    paddingX: "xsmall",
    id,
    children: /* @__PURE__ */ jsx(Text, { size: "xsmall", weight: "medium", icon: Icon, data, children: label })
  }
);
const JobStatusBadgeView = ({
  tooltip,
  label,
  tone,
  onImpression,
  onClickAnalytics,
  id
}) => {
  const trackClick = useCallback(() => {
    if (onClickAnalytics) {
      onClickAnalytics();
    }
  }, [onClickAnalytics]);
  const background = backgroundForTone[tone];
  const Icon = iconForTone[tone];
  useEffect(() => {
    if (onImpression) {
      onImpression();
    }
  }, [onImpression]);
  const badge = /* @__PURE__ */ jsx(
    JobStatusBadgeBaseView,
    {
      Icon: /* @__PURE__ */ jsx(Icon, {}),
      label,
      id,
      background,
      data: { testid: "job-status-badge" }
    }
  );
  return Boolean(tooltip) ? /* @__PURE__ */ jsx(
    TooltipRenderer,
    {
      id: `job-status-${id}-tooltip`,
      tooltip: /* @__PURE__ */ jsx(Text, { children: tooltip }),
      children: ({ triggerProps }) => /* @__PURE__ */ jsx(
        Box,
        {
          display: "inlineBlock",
          cursor: "pointer",
          onClick: onClickAnalytics ? trackClick : void 0,
          ...triggerProps,
          children: badge
        }
      )
    }
  ) : badge;
};
const useGetAppliedDateFormattedText = () => {
  const { t } = useTranslations(translations);
  return useCallback(
    (date) => t(`You applied on <JobAppliedDate>`, {
      JobAppliedDate: () => date
    }),
    [t]
  );
};
const useHasBadge = () => {
  const { job } = useJobDetailsViewContext();
  const shouldReduceDuplicatedApply = useShouldReduceDuplicateApply();
  return Boolean(
    job.isExpired || job.appliedDateFormatted && !shouldReduceDuplicatedApply || job.topApplicant
  );
};
const JobStatusBadge = () => {
  const { job, onAnalyticsEvent } = useJobDetailsViewContext();
  const shouldReduceDuplicatedApply = useShouldReduceDuplicateApply();
  const analytics = useAnalyticsHandlers(onAnalyticsEvent);
  const { t } = useTranslations(translations);
  const getAppliedDateFormattedText = useGetAppliedDateFormattedText();
  if (job.isExpired) {
    return /* @__PURE__ */ jsx(
      JobStatusBadgeView,
      {
        id: "expired",
        label: t("This job is no longer available"),
        tone: "critical"
      }
    );
  }
  if (job.appliedDateFormatted && !shouldReduceDuplicatedApply) {
    return /* @__PURE__ */ jsx(
      JobStatusBadgeView,
      {
        id: "applied",
        label: getAppliedDateFormattedText(job.appliedDateFormatted),
        tone: "positive"
      }
    );
  }
  if (job.topApplicant) {
    return /* @__PURE__ */ jsx(
      JobStatusBadgeView,
      {
        label: job.topApplicant.label,
        tone: "promote",
        id: "topApplicant",
        tooltip: job.topApplicant.description,
        onImpression: analytics.onTopApplicantBadgeImpression,
        onClickAnalytics: analytics.onTopApplicantBadgeClick
      }
    );
  }
  return null;
};
const ApplySection = ({
  size
}) => {
  const { job, ctas, shouldOpenApplyInNewTab, renderRelativePaths } = useJobDetailsViewContext();
  const shouldReduceDuplicateApply = useShouldReduceDuplicateApply();
  const shouldHideApplyButton = useIsApplyDisabled();
  const getAppliedDateFormattedText = useGetAppliedDateFormattedText();
  if (job.appliedDateFormatted && shouldReduceDuplicateApply) {
    return /* @__PURE__ */ jsx(Box, { height: "touchable", display: "flex", alignItems: "center", children: /* @__PURE__ */ jsx(
      FieldMessage,
      {
        id: "applied-date-message",
        tone: "positive",
        message: getAppliedDateFormattedText(job.appliedDateFormatted)
      }
    ) });
  }
  if (job.isExpired || shouldReduceDuplicateApply || shouldHideApplyButton) {
    return null;
  }
  return /* @__PURE__ */ jsx(
    ApplyButton,
    {
      job,
      ctas,
      shouldOpenApplyInNewTab,
      renderRelativePaths,
      size
    }
  );
};
const initialClassesObject = {};
let templateClasses = initialClassesObject;
const getTypeFromHref = (href) => contactTypeByProtocolFromHref(href);
const contactMatchSelector = "[data-contact-match=true]";
const iterateOverContactMatches = ({
  callback,
  DOMElement
}) => {
  const contactMatches = DOMElement.querySelectorAll(contactMatchSelector);
  contactMatches.forEach((element) => {
    const href = element.getAttribute("href");
    if (href) {
      const type = getTypeFromHref(href);
      callback(type, element);
    }
  });
};
const toCISImpression = ({
  contactMatches = []
}) => {
  const matches = [];
  if (!contactMatches) {
    return { matches };
  }
  for (const { type } of contactMatches) {
    if (!matches.includes(type)) {
      matches.push(type);
    }
  }
  return { matches: matches.sort() };
};
const PLACEHOLDER_DELIMITTER = "__CIS__";
const getPlaceholder = (i) => `${PLACEHOLDER_DELIMITTER}${i}${PLACEHOLDER_DELIMITTER}`;
const protocolByContactType = (contactType) => {
  switch (contactType) {
    case "Phone":
      return "tel";
    case "Email":
      return "mailto";
    case "Web":
      return "http";
    default:
      throw new Error(`unknown contact type ${contactType}`);
  }
};
const contactTypeByProtocolFromHref = (protocol) => {
  const protocolFromHref = protocol.split(":");
  switch (protocolFromHref[0]) {
    case "tel":
      return "Phone";
    case "mailto":
      return "Email";
    case "http":
      return "Web";
    default:
      throw new Error(`unknown protocol type ${protocol}`);
  }
};
const getClassAttributeValue = (type) => templateClasses[type] || "";
const getSharedAttributes = ({ type, value }) => `data-contact-match="true" href="${protocolByContactType(type)}:${value}"`;
const formatContact = (regexMatch, { type, value }) => {
  const sharedAttributes = getSharedAttributes({ type, value });
  const classAttributeValue = getClassAttributeValue(type);
  const classAttributeString = classAttributeValue && `class="${classAttributeValue}"`;
  const wrapperAttributes = join(
    compact([classAttributeString, sharedAttributes]),
    " "
  );
  return `<a ${wrapperAttributes}>${regexMatch}</a>`;
};
const templateReplacer = (template, contactMatch, customFormatter) => {
  const contact = escapeRegExp(contactMatch.value);
  const formatter = (regexMatch) => {
    if (regexMatch.startsWith("<")) {
      return regexMatch;
    }
    return customFormatter ? customFormatter() : formatContact(regexMatch, contactMatch);
  };
  return replace(
    template,
    new RegExp(`<img .*${contact}.*" />|${contact}`, "gi"),
    formatter
  );
};
const templateReducer = (template, contactMatches) => {
  const templateWithPlaceholders = contactMatches.reduce(
    (out, contactMatch, i) => templateReplacer(out, contactMatch, () => getPlaceholder(i)),
    template
  );
  return contactMatches.reduce(
    (out, contactMatch, i) => replace(
      out,
      new RegExp(getPlaceholder(i), "gi"),
      formatContact(contactMatch.value, contactMatch)
    ),
    templateWithPlaceholders
  );
};
const templateRenderer = () => (template = "", contactMatches = []) => templateReducer(template, contactMatches);
function useContactMatchInteractionEvents(ref, track) {
  const createHandler = useCallback(
    (eventType, contactType) => () => {
      track({
        contactType,
        eventType
      });
    },
    [track]
  );
  useEffect(() => {
    if (ref === null) {
      return;
    }
    const mappedEvents = [];
    const callback = (contactType, element) => {
      const clickHandler = createHandler("click", contactType);
      element.addEventListener("click", clickHandler);
      mappedEvents.push({ listener: clickHandler, type: "click", element });
      const copyHandler = createHandler("copy", contactType);
      element.addEventListener("copy", copyHandler);
      mappedEvents.push({ listener: copyHandler, type: "copy", element });
    };
    iterateOverContactMatches({ callback, DOMElement: ref });
    return () => {
      mappedEvents.forEach(({ element, type, listener }) => {
        element.removeEventListener(type, listener);
      });
    };
  }, [ref, createHandler]);
}
const JobDescription = (props) => {
  const {
    contactMatches,
    description,
    onContactMatchImpression,
    onContactMatchInteraction
  } = props;
  const [containerEl, setContainerEl] = useState(null);
  const renderedDesc = useMemo(() => {
    const view = templateRenderer();
    return view(description, contactMatches ?? []);
  }, [description, contactMatches]);
  useEffect(() => {
    if (!containerEl) {
      return;
    }
    const value = toCISImpression({ contactMatches });
    onContactMatchImpression(value);
  }, [containerEl]);
  useContactMatchInteractionEvents(containerEl, onContactMatchInteraction);
  return /* @__PURE__ */ jsx("div", { "data-automation": "jobAdDetails", ref: setContainerEl, children: /* @__PURE__ */ jsx(UnsafeJobHtmlRenderer, { children: renderedDesc }) });
};
const JobDisplayTags = ({
  displayTags
}) => displayTags == null ? void 0 : displayTags.map(({ background, label }, i) => /* @__PURE__ */ jsx(
  JobStatusBadgeBaseView,
  {
    background,
    label,
    id: `jobDisplayTags${i}`,
    data: { testid: `jobDisplayTags${i}` }
  },
  `jobDisplayTags${i}`
));
function encodeUrl(baseUrl, query) {
  const encodedQuery = Object.entries(query).reduce(
    (memo, [key, value], index) => `${memo}${index > 0 ? "&" : ""}${encodeURIComponent(
      key
    )}=${encodeURIComponent(value)}`,
    ""
  );
  return encodeURIComponent(
    `${baseUrl}${baseUrl.indexOf("?") === -1 ? "?" : "&"}${encodedQuery}`
  );
}
const useShareHandler = (options) => {
  const { t } = useTranslations(translations$1);
  const [href, width, height] = useMemo(() => {
    switch (options.channel) {
      case "facebook": {
        const url = encodeUrl(options.url, { share: "fb" });
        return [`https://www.facebook.com/sharer.php?u=${url}`, 620, 436];
      }
      case "linkedin": {
        const url = encodeUrl(options.url, { share: "li" });
        return [
          `https://www.linkedin.com/shareArticle?mini=true&url=${url}`,
          620,
          338
        ];
      }
      case "twitter": {
        const url = encodeUrl(options.url, { share: "twt" });
        const text = encodeURIComponent(t("Check out this job on SEEK:"));
        return [`https://twitter.com/share?text=${text}&url=${url}`, 620, 338];
      }
      default: {
        return [];
      }
    }
  }, [options, t]);
  const emailShare = useCallback(
    (event) => {
      if (event) {
        event.preventDefault();
      }
      window.location.href = options.url;
      if (options.track) {
        options.track();
      }
    },
    [options]
  );
  const socialShare = useCallback(
    (event) => {
      if (event) {
        event.preventDefault();
      }
      window.open(
        href,
        "sharer",
        `toolbar=0,status=0,width=${width},height=${height}`
      );
      if (options.track) {
        options.track();
      }
    },
    [href, height, width, options]
  );
  return options.channel === "email" ? emailShare : socialShare;
};
const getEmailShareMailtoUrl = ({
  jobTitle,
  jobShareUrl,
  emailSubjectText,
  emailBodyOpeningText,
  emailBodySectionText
}) => {
  const emailSubject = encodeURIComponent(`${emailSubjectText} - ${jobTitle}`);
  const emailBody = encodeURIComponent(
    `${emailBodyOpeningText}

${emailBodySectionText}

${jobTitle} -
${jobShareUrl}`
  );
  return `mailto:%20?subject=${emailSubject}&body=${emailBody}`;
};
const OptionsDropdownMenu = ({
  job,
  onAnalyticsEvent = () => {
  }
}) => {
  const {
    reportJobAd: { onReportJobAdDropdownClick }
  } = useJobDetailsViewContext();
  const analytics = useAnalyticsHandlers(onAnalyticsEvent);
  const { t } = useTranslations(translations$2);
  const jobUrl = useJobUrl({
    id: job.id,
    isExpired: job.isExpired
  });
  const url = job.shareLink || job.url || jobUrl;
  const onFbClick = useShareHandler({
    channel: "facebook",
    url,
    track: analytics.onShareFacebookClick
  });
  const onXClick = useShareHandler({
    channel: "twitter",
    url,
    track: analytics.onShareTwitterClick
  });
  const onLinkedInClick = useShareHandler({
    channel: "linkedin",
    url,
    track: analytics.onShareLinkedInClick
  });
  const emailShareUrl = useMemo(
    () => getEmailShareMailtoUrl({
      jobTitle: t("{jobTitle}", { jobTitle: job.title }),
      jobShareUrl: url,
      emailSubjectText: t("I found you a job on SEEK!"),
      emailBodyOpeningText: t("Hey,"),
      emailBodySectionText: t(
        "I found this job via SEEK and thought of you."
      )
    }),
    [job, url, t]
  );
  const onEmailClick = useShareHandler({
    channel: "email",
    url: emailShareUrl,
    track: analytics.onShareEmailClick
  });
  return /* @__PURE__ */ jsxs(
    MenuRenderer,
    {
      align: "right",
      offsetSpace: "xsmall",
      trigger: (triggerProps) => /* @__PURE__ */ jsx(
        ButtonIcon,
        {
          id: "OptionsDropdownMenuButton",
          icon: /* @__PURE__ */ jsx(IconOverflow, {}),
          label: t("Share or report ad"),
          data: { automation: "option-drop-down-menu" },
          ...triggerProps
        }
      ),
      children: [
        /* @__PURE__ */ jsx(
          MenuItem,
          {
            icon: /* @__PURE__ */ jsx(IconMail, {}),
            onClick: onEmailClick,
            data: { automation: "send-job-link" },
            children: t("Email")
          }
        ),
        /* @__PURE__ */ jsx(
          MenuItem,
          {
            icon: /* @__PURE__ */ jsx(
              IconSocialFacebook,
              {
                title: t("Opens in a new tab"),
                titleId: "facebookId"
              }
            ),
            onClick: onFbClick,
            children: t("Facebook")
          }
        ),
        /* @__PURE__ */ jsx(
          MenuItem,
          {
            icon: /* @__PURE__ */ jsx(IconSocialX, { title: t("Opens in a new tab"), titleId: "twitterId" }),
            onClick: onXClick,
            children: t("Twitter")
          }
        ),
        /* @__PURE__ */ jsx(
          MenuItem,
          {
            icon: /* @__PURE__ */ jsx(
              IconSocialLinkedIn,
              {
                title: t("Opens in a new tab"),
                titleId: "linkedInId"
              }
            ),
            onClick: onLinkedInClick,
            children: t("LinkedIn")
          }
        ),
        /* @__PURE__ */ jsx(MenuItemDivider, {}),
        /* @__PURE__ */ jsx(MenuItem, { icon: /* @__PURE__ */ jsx(IconFlag, {}), onClick: onReportJobAdDropdownClick, children: t("Report job ad") })
      ]
    }
  );
};
const useOptionsBar = () => {
  const {
    embedded,
    hideOpenInNewTab,
    job,
    jobUrlOpts,
    onAnalyticsEvent,
    renderRelativePaths
  } = useJobDetailsViewContext();
  const newTabUrl = useJobUrl({
    id: job.id,
    jobUrlOpts,
    renderRelativePaths,
    overrideValues: {
      origin: jobUrlOriginValues.showNewTab
    },
    isExpired: job.isExpired
  });
  const jobUrl = job.url || newTabUrl;
  const showOpenInNewTab = embedded || !hideOpenInNewTab;
  return {
    job,
    newTabUrl: jobUrl,
    onAnalyticsEvent,
    showOpenInNewTab
  };
};
const OptionsBar = () => {
  const { job, newTabUrl, onAnalyticsEvent, showOpenInNewTab } = useOptionsBar();
  const { t } = useTranslations(translations$3);
  return /* @__PURE__ */ jsxs(Columns, { space: "xsmall", alignY: "center", children: [
    showOpenInNewTab ? /* @__PURE__ */ jsx(Column, { width: "content", children: /* @__PURE__ */ jsx(Link, { href: newTabUrl, target: "_blank", tabIndex: -1, children: /* @__PURE__ */ jsx(
      ButtonIcon,
      {
        id: "newTabButton",
        icon: /* @__PURE__ */ jsx(IconNewWindow, {}),
        label: t("New tab")
      }
    ) }) }) : null,
    /* @__PURE__ */ jsx(Column, { width: "content", children: /* @__PURE__ */ jsx(OptionsDropdownMenu, { job, onAnalyticsEvent }) })
  ] });
};
const useReasons = () => {
  const { t } = useTranslations(translations$1);
  return {
    Fraudulent: t("Fraudulent"),
    Discrimination: t("Discrimination"),
    Misleading: t("Misleading")
  };
};
const FORM_ID = "report-job-ad-form";
const MAX_COMMENT_LENTH = 400;
const INITIAL_FORM_VALUES = {
  email: "",
  reason: "",
  comment: ""
};
const { FormProvider } = createForm(FORM_ID);
const ReportJobAd = (props) => {
  const { t } = useTranslations(translations$1);
  const {
    reportJobAd: { ref: reportJobAdRef, setOnReportJobAdDropdownClick },
    featureFlags
  } = useJobDetailsViewContext();
  const {
    handleReset,
    initialExpansion,
    isPDPAConsentRequired,
    isVerified,
    status = "initial",
    validationErrors,
    renderRelativePaths
  } = props;
  const shouldOpenLinkInNewTab = featureFlags == null ? void 0 : featureFlags.shouldOpenLinkInNewTab;
  const [formVisible, setFormVisible] = useState(initialExpansion);
  useEffect(() => {
    setOnReportJobAdDropdownClick(() => () => {
      var _a;
      if ((_a = reportJobAdRef.current) == null ? void 0 : _a.scrollIntoView) {
        reportJobAdRef.current.scrollIntoView({
          behavior: "smooth",
          block: "nearest"
        });
        setFormVisible(true);
      }
    });
  }, [reportJobAdRef, setOnReportJobAdDropdownClick]);
  const required = {
    getErrorMessage: ({ validatorProps: { requiredLabel = "" } }) => Promise.resolve(requiredLabel),
    test: ({ value }) => Boolean(value)
  };
  const requiredSelection = {
    getErrorMessage: ({ validatorProps: { requiredSelectionLabel = "" } }) => Promise.resolve(requiredSelectionLabel),
    test: ({ value }) => Boolean(value.length)
  };
  const email = useField({
    id: "email",
    form: FORM_ID,
    validators: [required, isEmail],
    validatorProps: {
      requiredLabel: t("We need an email address to get back to you.")
    }
  });
  const reason = useField({
    id: "reason",
    form: FORM_ID,
    validators: [requiredSelection],
    validatorProps: {
      requiredSelectionLabel: t("We need a reason for reporting this job.")
    }
  });
  const comment = useField({
    id: "comment",
    form: FORM_ID,
    validators: [maxLength, required],
    validatorProps: {
      max: MAX_COMMENT_LENTH,
      requiredLabel: t("This field is required")
    }
  });
  const reasons = useReasons();
  const onCancel = useCallback(() => {
    handleReset();
    setFormVisible(false);
  }, [handleReset, setFormVisible]);
  const emailInputRef = useRef(null);
  const reasonInputRef = useRef(null);
  const commentInputRef = useRef(null);
  const inputRefs = useMemo(
    () => ({
      email: emailInputRef,
      reason: reasonInputRef,
      comment: commentInputRef
    }),
    [emailInputRef, reasonInputRef, commentInputRef]
  );
  useEffect(() => {
    validationErrors.some((errorKey) => {
      const inputRef = inputRefs[errorKey];
      if (inputRef == null ? void 0 : inputRef.current) {
        inputRef.current.focus();
        return true;
      }
      return false;
    });
  }, [validationErrors, inputRefs]);
  const securityPrivacyPath = useMelwaysLink({
    path: "/security-privacy",
    product: "candidate",
    absolute: !renderRelativePaths
  });
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(HiddenVisually, { children: /* @__PURE__ */ jsx("h2", { children: t("Report this job advert") }) }),
    /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
      /* @__PURE__ */ jsx(Text, { size: "large", weight: "medium", children: t("Be careful") }),
      /* @__PURE__ */ jsx(Text, { children: isVerified ? t(
        "Don’t provide your bank or credit card details when applying for jobs."
      ) : t(
        "This job ad has not been subjected to our hirer verification process..."
      ) }),
      /* @__PURE__ */ jsx(Text, { children: /* @__PURE__ */ jsx(
        TextLink,
        {
          href: securityPrivacyPath,
          rel: "nofollow",
          weight: "weak",
          hitArea: "large",
          target: shouldOpenLinkInNewTab ? "_blank" : void 0,
          children: t("Learn how to protect yourself")
        }
      ) }),
      status === "success" ? /* @__PURE__ */ jsx(Notice, { tone: "positive", children: /* @__PURE__ */ jsx(Text, { data: { automation: "report-job-ad-success-message" }, children: t(
        "Thank you. Your job report has been forwarded to our job quality team."
      ) }) }) : null,
      status === "error" ? /* @__PURE__ */ jsx(Notice, { tone: "critical", children: /* @__PURE__ */ jsx(Text, { data: { automation: "report-job-ad-error-message" }, children: t(
        "It looks like something went wrong and your report could not be sent."
      ) }) }) : null,
      status === "initial" ? /* @__PURE__ */ jsx(Hidden, { print: true, children: /* @__PURE__ */ jsx(Box, { marginTop: "small", children: /* @__PURE__ */ jsx(
        Disclosure,
        {
          id: "toggleReportJobAdForm",
          expandLabel: t("Report this job ad"),
          data: {
            automation: "report-job-ad-toggle"
          },
          space: "large",
          onToggle: setFormVisible,
          expanded: formVisible,
          children: /* @__PURE__ */ jsx(
            "form",
            {
              onSubmit: props.handleSubmit,
              "data-automation": "report-job-ad-form",
              children: /* @__PURE__ */ jsxs(Stack, { space: "large", children: [
                /* @__PURE__ */ jsx(
                  TextField,
                  {
                    ref: emailInputRef,
                    value: email.value,
                    onChange: email.onChange,
                    message: email.errorMessage || void 0,
                    tone: !email.valid ? "critical" : void 0,
                    id: "email",
                    label: t("Your email address"),
                    data: { automation: "report-job-ad-email" }
                  }
                ),
                /* @__PURE__ */ jsx(
                  Dropdown,
                  {
                    id: "reason",
                    label: t("Reason for reporting job"),
                    ref: reasonInputRef,
                    placeholder: t("Please select"),
                    data: { automation: "report-job-ad-reason" },
                    value: reason.value,
                    onChange: reason.onChange,
                    message: reason.errorMessage || void 0,
                    tone: !reason.valid ? "critical" : void 0,
                    children: Object.entries(reasons).map(([key, value]) => /* @__PURE__ */ jsx("option", { value: key, children: value }, key))
                  }
                ),
                /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
                  /* @__PURE__ */ jsx(
                    Textarea,
                    {
                      id: "comment",
                      ref: commentInputRef,
                      label: t("Additional comments"),
                      data: { automation: "report-job-ad-comment" },
                      value: comment.value,
                      onChange: comment.onChange,
                      message: comment.errorMessage || void 0,
                      tone: !comment.valid ? "critical" : void 0,
                      characterLimit: MAX_COMMENT_LENTH
                    }
                  ),
                  /* @__PURE__ */ jsx(
                    FieldMessage,
                    {
                      id: "comment-message",
                      tone: "caution",
                      message: t(
                        "To help fast track investigation, please include here any other relevant details that prompted you to report this job ad as fraudulent / misleading / discriminatory."
                      )
                    }
                  )
                ] }),
                isPDPAConsentRequired ? /* @__PURE__ */ jsx(
                  Text,
                  {
                    data: {
                      automation: "report-job-ad-pdpa-consent-label"
                    },
                    children: t(
                      "You confirm that you’re at least 20 years old or have parental consent for JobsDB and affiliates to process your personal data. By submitting this information, you agree to our Privacy Policy.",
                      {
                        TextLink: (children) => /* @__PURE__ */ jsx(
                          TextLink,
                          {
                            href: "/privacy",
                            rel: "nofollow",
                            weight: "weak",
                            hitArea: "large",
                            children
                          }
                        )
                      }
                    )
                  }
                ) : null,
                /* @__PURE__ */ jsxs(Actions, { children: [
                  /* @__PURE__ */ jsx(
                    Button,
                    {
                      type: "submit",
                      tone: "formAccent",
                      data: {
                        automation: "report-job-ad-submit"
                      },
                      children: t("Report job")
                    }
                  ),
                  /* @__PURE__ */ jsx(
                    Button,
                    {
                      data: {
                        automation: "report-job-ad-cancel"
                      },
                      onClick: onCancel,
                      variant: "transparent",
                      tone: "formAccent",
                      children: t("Cancel")
                    }
                  )
                ] })
              ] })
            }
          )
        }
      ) }) }) : null
    ] })
  ] });
};
const ReportJobAd$1 = (props) => {
  const {
    reportJobAd: { ref }
  } = useJobDetailsViewContext();
  const {
    initialExpansion = false,
    initialStatus = "initial",
    initialValues = INITIAL_FORM_VALUES,
    isPDPAConsentRequired,
    isVerified,
    onSubmit,
    renderRelativePaths
  } = props;
  const { language = "en" } = useLanguage();
  const [status, setStatus] = useState(initialStatus);
  const [validationErrors, setValidationErrors] = useState([]);
  const submitHandler = useCallback(
    (values) => {
      onSubmit(values).then((success) => {
        setStatus(success ? "success" : "error");
      }).catch(() => setStatus("error"));
      return;
    },
    [onSubmit, setStatus]
  );
  const key = useMemo(() => JSON.stringify(initialValues), [initialValues]);
  return /* @__PURE__ */ jsx(Box, { ref, children: /* @__PURE__ */ jsx(
    FormProvider,
    {
      initialValues,
      language,
      onSubmitFail: (errors) => setValidationErrors(errors),
      children: ({ handleSubmit, resetForm }) => /* @__PURE__ */ jsx(
        ReportJobAd,
        {
          initialExpansion,
          handleReset: resetForm,
          handleSubmit: handleSubmit(submitHandler),
          status,
          validationErrors,
          isPDPAConsentRequired,
          isVerified,
          renderRelativePaths
        }
      )
    },
    key
  ) });
};
const noop$1 = () => {
};
const SaveButton = ({
  job,
  ctas,
  size = "standard",
  mobileOptions
}) => {
  const { t } = useTranslations(translations$1);
  const buttonText = job.isSaved ? t("Unsave") : t("Save");
  const { displayText = false, displayIcon = true } = mobileOptions ?? {};
  return job.isExpired || Boolean(job.appliedDateFormatted) ? null : /* @__PURE__ */ jsxs(
    Button,
    {
      "aria-expanded": job.isSaved || false,
      onClick: (ctas == null ? void 0 : ctas.onToggleSaveJob) ?? noop$1,
      variant: "soft",
      tone: "formAccent",
      data: { testid: "jdv-savedjob" },
      "aria-label": job.isSaved ? t("Unsave {jobTitle} at {jobCompanyName}", {
        jobTitle: job.title,
        jobCompanyName: job.company.name
      }) : t("Save {jobTitle} at {jobCompanyName}", {
        jobTitle: job.title,
        jobCompanyName: job.company.name
      }),
      size,
      children: [
        /* @__PURE__ */ jsxs(Box, { display: { mobile: "block", tablet: "none" }, children: [
          displayIcon ? /* @__PURE__ */ jsx(
            IconBookmark,
            {
              tone: job.isSaved ? "brandAccent" : void 0,
              active: job.isSaved
            }
          ) : null,
          displayText ? buttonText : null
        ] }),
        /* @__PURE__ */ jsx(Box, { display: { mobile: "none", tablet: "block" }, children: buttonText })
      ]
    }
  );
};
const useAddedQualities = () => {
  const [addedQualities, setAddedQualities] = useState({
    inferred: [],
    unmatched: []
  });
  const addToAddedInferredQualities = useCallback(
    (quality) => {
      setAddedQualities((prev) => ({
        inferred: [...prev.inferred, quality],
        unmatched: prev.unmatched
      }));
    },
    [setAddedQualities]
  );
  const removeFromAddedInferredQualities = useCallback(
    (quality) => {
      setAddedQualities((prev) => ({
        inferred: prev.inferred.filter((item) => item !== quality),
        unmatched: prev.unmatched
      }));
    },
    [setAddedQualities]
  );
  const addToAddedUnmatchedQualities = useCallback(
    (quality) => {
      setAddedQualities((prev) => ({
        inferred: prev.inferred,
        unmatched: [...prev.unmatched, quality]
      }));
    },
    [setAddedQualities]
  );
  const removeFromAddedUnmatchedQualities = useCallback(
    (quality) => {
      setAddedQualities((prev) => ({
        inferred: prev.inferred,
        unmatched: prev.unmatched.filter((item) => item !== quality)
      }));
    },
    [setAddedQualities]
  );
  const updateAddedQualities = {
    inferred: {
      addToAddedQualities: addToAddedInferredQualities,
      removeFromAddedQualities: removeFromAddedInferredQualities
    },
    unmatched: {
      addToAddedQualities: addToAddedUnmatchedQualities,
      removeFromAddedQualities: removeFromAddedUnmatchedQualities
    }
  };
  return { updateAddedQualities, addedQualities };
};
const calculateTotalAndPresence = (array) => {
  const total = (array == null ? void 0 : array.length) || 0;
  return { total, hasItems: total > 0 };
};
const determineQualitiesWhenCollapsed = (matched, inferred, unmatched) => {
  if (matched.hasItems) {
    return {
      type: "matched",
      total: matched.total
    };
  }
  if (inferred.hasItems) {
    return {
      type: "inferred",
      total: inferred.total
    };
  }
  if (unmatched.hasItems) {
    return {
      type: "unmatched",
      total: unmatched.total
    };
  }
  return {
    type: "none",
    total: 0
  };
};
const useQualitiesWhenCollapsed = () => {
  const { matched, inferred, unmatched } = useQualities();
  const _matched = calculateTotalAndPresence(matched);
  const _inferred = calculateTotalAndPresence(inferred);
  const _unmatched = calculateTotalAndPresence(unmatched);
  return determineQualitiesWhenCollapsed(_matched, _inferred, _unmatched);
};
const noop = () => {
};
const initialState = {
  isSkillsLimitErrorShown: false,
  setIsSkillsLimitErrorShown: noop,
  analyticsParams: {
    keywords: [],
    totalAddedQualities: 0,
    totalMatchedQualities: 0
  },
  updateToProfile: {},
  addedQualities: {
    inferred: [],
    unmatched: []
  },
  updateAddedQualities: {
    inferred: {
      addToAddedQualities: noop,
      removeFromAddedQualities: noop
    },
    unmatched: {
      addToAddedQualities: noop,
      removeFromAddedQualities: noop
    }
  },
  limit: 0,
  isExpanded: false,
  setIsExpanded: noop,
  qualitiesWhenCollapsed: {
    total: 0,
    type: "none"
  }
};
const SkillsMatchSignedInContext = createContext(initialState);
const useSkillsMatchSignedInContext = () => useContext(SkillsMatchSignedInContext);
const useAddedQualitiesByType = (type) => {
  const { addedQualities, updateAddedQualities } = useSkillsMatchSignedInContext();
  return {
    addedQualities: addedQualities[type],
    ...updateAddedQualities[type]
  };
};
const SkillsMatchContextProvider = ({
  children,
  onAddQuality,
  onRemoveQuality
}) => {
  const { matched } = useQualities();
  const { embedded } = useJobDetailsViewContext();
  const [isSkillsLimitErrorShown, setIsSkillsLimitErrorShown] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const { updateAddedQualities, addedQualities } = useAddedQualities();
  const totalAddedQualities = useMemo(
    () => addedQualities.inferred.length + addedQualities.unmatched.length,
    [addedQualities]
  );
  const keywords = useMemo(
    () => [...addedQualities.inferred, ...addedQualities.unmatched].map(
      (q) => q.keyword.text
    ),
    [addedQualities]
  );
  const updateToProfile = {
    add: async (quality) => {
      const { ontologyId: id, text } = quality.keyword;
      return onAddQuality == null ? void 0 : onAddQuality({
        keyword: {
          ontologyId: id,
          text
        }
      });
    },
    remove: async (quality) => {
      const { ontologyId: id, text } = quality.keyword;
      return onRemoveQuality == null ? void 0 : onRemoveQuality({
        keyword: {
          ontologyId: id,
          text
        }
      });
    }
  };
  const responsiveValue = useResponsiveValue();
  const limit = responsiveValue({
    mobile: 0,
    tablet: 3,
    desktop: embedded ? 3 : 4
  }) || 0;
  const qualitiesWhenCollapsed = useQualitiesWhenCollapsed();
  const value = {
    isSkillsLimitErrorShown,
    setIsSkillsLimitErrorShown,
    analyticsParams: {
      keywords,
      totalAddedQualities,
      totalMatchedQualities: matched.length
    },
    updateToProfile,
    addedQualities,
    updateAddedQualities,
    limit,
    isExpanded,
    setIsExpanded,
    qualitiesWhenCollapsed
  };
  return /* @__PURE__ */ jsx(SkillsMatchSignedInContext.Provider, { value, children });
};
const SkillsMatchSkeleton = () => /* @__PURE__ */ jsxs(
  Box,
  {
    borderRadius: "large",
    boxShadow: "borderNeutralLight",
    padding: "large",
    "data-testid": "skills-match-skeleton",
    children: [
      /* @__PURE__ */ jsx(Box, { paddingBottom: "large", children: /* @__PURE__ */ jsx(SkeletonLine, { type: "text", width: 150 }) }),
      /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
        /* @__PURE__ */ jsx(SkeletonLine, { type: "text", width: 250, size: "xsmall" }),
        /* @__PURE__ */ jsxs(Inline, { space: "small", children: [
          /* @__PURE__ */ jsx(SkeletonLine, { type: "text", width: 100, size: "large" }),
          /* @__PURE__ */ jsx(SkeletonLine, { type: "text", width: 100, size: "large" }),
          /* @__PURE__ */ jsx(SkeletonLine, { type: "text", width: 100, size: "large" }),
          /* @__PURE__ */ jsx(SkeletonLine, { type: "text", width: 100, size: "large" }),
          /* @__PURE__ */ jsx(SkeletonLine, { type: "text", width: 100, size: "large" })
        ] })
      ] })
    ]
  }
);
const ErrorMessage = () => {
  const { t } = useTranslations(translations$4);
  return /* @__PURE__ */ jsx(Text, { size: "small", tone: "critical", icon: /* @__PURE__ */ jsx(IconCritical, {}), children: t(
    "We are having trouble loading your matching skills. Please check back later."
  ) });
};
const AlertMessages = ({
  isSkillsLimitErrorShown,
  hasResume
}) => {
  const { t } = useTranslations(translations$5);
  return /* @__PURE__ */ jsx(Box, { paddingBottom: "small", children: /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
    isSkillsLimitErrorShown && /* @__PURE__ */ jsx(Text, { size: "small", tone: "critical", icon: /* @__PURE__ */ jsx(IconCritical, {}), children: t("You cant have more than 50 skills") }),
    !hasResume && /* @__PURE__ */ jsx(Text, { size: "small", tone: "info", icon: /* @__PURE__ */ jsx(IconInfo, {}), children: t("Upload a current résumé to your profile to see better matches") })
  ] }) });
};
const Disclaimer = () => {
  const { t } = useTranslations(translations$6);
  return /* @__PURE__ */ jsx(Text, { size: "xsmall", children: /* @__PURE__ */ jsx(Secondary, { children: t(
    "Help us match better. We match your skills and credentials based on your profile and default resumé but we might not always get it right."
  ) }) });
};
const FeedbackThumb = ({
  direction,
  id,
  label,
  activeThumb,
  setActiveThumb
}) => {
  const { onAnalyticsEvent } = useJobDetailsViewContext();
  const analytics = useAnalyticsHandlers(onAnalyticsEvent);
  return /* @__PURE__ */ jsx(
    ButtonIcon,
    {
      bleed: true,
      variant: activeThumb === direction ? "soft" : "transparent",
      size: "standard",
      icon: /* @__PURE__ */ jsx(IconThumb, { direction }),
      label,
      id,
      onClick: () => {
        analytics.onSkillsMatchFeedbackPressed({
          currentPage: "job details",
          linkAction: direction === "up" ? "positive" : "negative"
        });
        setActiveThumb((prev) => prev === direction ? null : direction);
      }
    }
  );
};
const FeedbackIndicator = () => {
  const { t } = useTranslations(translations$7);
  const [activeThumb, setActiveThumb] = useState(null);
  return /* @__PURE__ */ jsx(Inline, { space: "xsmall", alignY: "center", children: activeThumb !== null ? /* @__PURE__ */ jsx(Text, { size: "small", children: t("Thank you for helping us improve the matching results.") }) : /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(Text, { size: "small", children: t("Was this skills match accurate?") }),
    /* @__PURE__ */ jsxs(
      Box,
      {
        flexDirection: "row",
        display: "flex",
        justifyContent: "center",
        gap: "small",
        children: [
          /* @__PURE__ */ jsx(
            FeedbackThumb,
            {
              id: "thumbs-up",
              direction: "up",
              label: t("Yes"),
              activeThumb,
              setActiveThumb
            }
          ),
          /* @__PURE__ */ jsx(
            FeedbackThumb,
            {
              id: "thumbs-down",
              direction: "down",
              label: t("No"),
              activeThumb,
              setActiveThumb
            }
          )
        ]
      }
    )
  ] }) });
};
const currentPage$1 = "job details";
const currentPageSection$1 = "skills match";
const alertShownType = "notice";
const onSkillsMatchAlert = ({
  analytics,
  alertShownTone = "info",
  alertShownMessage
}) => {
  analytics.onSkillsMatchAlert({
    currentPage: currentPage$1,
    currentPageSection: currentPageSection$1,
    alertShownTone,
    alertShownType,
    alertShownMessage
  });
};
const onSkillsMatchProfileUpdated = ({
  analytics,
  type,
  keywords,
  totalMatchedQualities,
  totalAddedQualities
}) => {
  analytics.onSkillsMatchProfileUpdated({
    currentPage: currentPage$1,
    currentPageSection: currentPageSection$1,
    updateType: type,
    actionOrigin: "jdv - skills match",
    skillsAdded: keywords,
    skillsStartTotal: totalMatchedQualities,
    skillsFromSuggestedTotal: totalAddedQualities,
    skillsCompleteTotal: type === "add" ? totalMatchedQualities + totalAddedQualities : totalMatchedQualities - totalAddedQualities
  });
};
const useSendAnalytics = () => {
  const { t } = useTranslations(translations$8);
  const { analyticsParams } = useSkillsMatchSignedInContext();
  const { onAnalyticsEvent } = useJobDetailsViewContext();
  const analytics = useAnalyticsHandlers(onAnalyticsEvent);
  const sendAnalytics = useCallback(
    (actionType) => {
      const { keywords, totalMatchedQualities, totalAddedQualities } = analyticsParams;
      if (actionType === "error") {
        onSkillsMatchAlert({
          analytics,
          alertShownTone: "critical",
          alertShownMessage: t(
            "There was a problem adding this item. Try refreshing or try again later."
          )
        });
      } else {
        onSkillsMatchProfileUpdated({
          analytics,
          type: actionType,
          keywords,
          totalMatchedQualities,
          totalAddedQualities
        });
      }
    },
    [analytics, analyticsParams, t]
  );
  return sendAnalytics;
};
const useShowToast = () => {
  const { t } = useTranslations(translations$8);
  const getMessage = useCallback(
    (toastType) => {
      switch (toastType) {
        case "added":
          return t("Added to profile");
        case "removed":
          return t("Removed from profile");
        default:
          return t(
            "There was a problem adding this item. Try refreshing or try again later."
          );
      }
    },
    [t]
  );
  const _showToast = useToast();
  const showToast = useCallback(
    (toastType) => {
      _showToast({
        message: getMessage(toastType),
        tone: toastType === "error" ? "critical" : "positive",
        key: `quality-${toastType}`
      });
    },
    [_showToast, getMessage]
  );
  return showToast;
};
const useSelectableQualities = (type) => {
  const { setIsSkillsLimitErrorShown, updateToProfile } = useSkillsMatchSignedInContext();
  const {
    addedQualities: selectedQualities,
    addToAddedQualities,
    removeFromAddedQualities
  } = useAddedQualitiesByType(type);
  const showToast = useShowToast();
  const sendAnalytics = useSendAnalytics();
  const selectQuality = async (quality) => {
    var _a;
    addToAddedQualities(quality);
    const res = await ((_a = updateToProfile.add) == null ? void 0 : _a.call(updateToProfile, quality));
    if (res === "UpdateSkillSuccess") {
      showToast("added");
      sendAnalytics("add");
      return;
    }
    if (res === "SkillLimitReached") {
      setIsSkillsLimitErrorShown(true);
    } else if (res === "UpdateSkillError") {
      showToast("error");
      sendAnalytics("error");
    }
    removeFromAddedQualities(quality);
  };
  const deselectQuality = async (quality) => {
    var _a;
    removeFromAddedQualities(quality);
    const res = await ((_a = updateToProfile.remove) == null ? void 0 : _a.call(updateToProfile, quality));
    if (res === "UpdateSkillSuccess") {
      sendAnalytics("remove");
      showToast("removed");
      return;
    }
    showToast("error");
    sendAnalytics("error");
    addToAddedQualities(quality);
  };
  return {
    selectedQualities,
    selectQuality,
    deselectQuality
  };
};
const SelectableQualities = ({
  type
}) => {
  const { limit, isExpanded } = useSkillsMatchSignedInContext();
  const allQualities = useQualities();
  const qualities = allQualities[type];
  const { selectedQualities, selectQuality, deselectQuality } = useSelectableQualities(type);
  const displayedQualities = useMemo(
    () => qualities.slice(0, limit),
    [limit, qualities]
  );
  const hiddenQualities = useMemo(
    () => qualities.slice(limit, qualities.length),
    [limit, qualities]
  );
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    displayedQualities.map((item) => {
      const isAdded = selectedQualities.includes(item);
      return /* @__PURE__ */ jsx(
        QualityTag,
        {
          item,
          isAdded,
          onUserAction: isAdded ? deselectQuality : selectQuality
        },
        item.keyword.ontologyId
      );
    }),
    isExpanded && hiddenQualities.map((item) => {
      const isAdded = selectedQualities.includes(item);
      return /* @__PURE__ */ jsx(
        QualityTag,
        {
          item,
          isAdded,
          onUserAction: isAdded ? deselectQuality : selectQuality
        },
        item.keyword.ontologyId
      );
    })
  ] });
};
const OtherQualities = ({
  inferred,
  unmatched,
  isInferredQualitiesTop,
  isUnmatchedQualitiesTop
}) => {
  const { t: tSkillsMatch } = useTranslations(translations$9);
  const { t: tSkillsMatchSignedIn } = useTranslations(
    translations$6
  );
  return /* @__PURE__ */ jsxs(Stack, { space: "large", children: [
    (inferred == null ? void 0 : inferred.length) !== 0 && !isInferredQualitiesTop && /* @__PURE__ */ jsxs(Stack, { space: "xsmall", children: [
      /* @__PURE__ */ jsx(Text, { size: "small", children: tSkillsMatchSignedIn("Matches based on your career history") }),
      /* @__PURE__ */ jsx(Inline, { space: "xxsmall", children: /* @__PURE__ */ jsx(SelectableQualities, { type: "inferred" }) })
    ] }),
    (unmatched == null ? void 0 : unmatched.length) !== 0 && !isUnmatchedQualitiesTop && /* @__PURE__ */ jsxs(Stack, { space: "xsmall", children: [
      /* @__PURE__ */ jsx(Text, { size: "small", children: tSkillsMatch("Not found in your profile") }),
      /* @__PURE__ */ jsx(Inline, { space: "xxsmall", children: /* @__PURE__ */ jsx(SelectableQualities, { type: "unmatched" }) })
    ] })
  ] });
};
const ExpandedContent = ({
  shouldShowOtherQualities,
  inferred,
  unmatched,
  isInferredQualitiesTop,
  isUnmatchedQualitiesTop,
  isExpanded,
  isSkillsLimitErrorShown,
  hasResume,
  hideAllOnClick
}) => {
  const { t } = useTranslations(translations$a);
  const { onAnalyticsEvent } = useJobDetailsViewContext();
  const analytics = useAnalyticsHandlers(onAnalyticsEvent);
  return /* @__PURE__ */ jsxs(Stack, { space: "large", children: [
    shouldShowOtherQualities && /* @__PURE__ */ jsx(
      OtherQualities,
      {
        inferred,
        unmatched,
        isInferredQualitiesTop,
        isUnmatchedQualitiesTop,
        isExpanded
      }
    ),
    /* @__PURE__ */ jsxs(Stack, { space: ["medium", "small"], children: [
      !hasResume || isSkillsLimitErrorShown ? /* @__PURE__ */ jsx(
        AlertMessages,
        {
          isSkillsLimitErrorShown,
          hasResume
        }
      ) : null,
      /* @__PURE__ */ jsx(FeedbackIndicator, {}),
      /* @__PURE__ */ jsx(Disclaimer, {})
    ] }),
    /* @__PURE__ */ jsx(Box, { paddingY: "xxsmall", children: /* @__PURE__ */ jsx(Text, { size: "small", children: /* @__PURE__ */ jsx(
      TextLinkButton,
      {
        weight: "weak",
        icon: /* @__PURE__ */ jsx(IconChevron, { direction: "up" }),
        onClick: () => {
          analytics.onSkillsMatchAccordionPressed({
            linkAction: "collapse",
            linkSection: "skills match"
          });
          hideAllOnClick();
        },
        iconPosition: "trailing",
        children: t("Hide all")
      }
    ) }) })
  ] });
};
const ShowMoreButton = ({
  onClick: onClickProp
}) => {
  const { limit, qualitiesWhenCollapsed } = useSkillsMatchSignedInContext();
  const { onAnalyticsEvent } = useJobDetailsViewContext();
  const analytics = useAnalyticsHandlers(onAnalyticsEvent);
  const { t } = useTranslations(translations$b);
  const onClick = useCallback(
    (mouseEvent) => {
      analytics.onSkillsMatchAccordionPressed({
        linkAction: "expand",
        linkSection: "skills match"
      });
      onClickProp == null ? void 0 : onClickProp(mouseEvent);
    },
    [analytics, onClickProp]
  );
  if (!qualitiesWhenCollapsed.total) {
    return null;
  }
  const numOfQualitiesToHide = qualitiesWhenCollapsed.total - limit;
  const showMoreText = limit === 0 || qualitiesWhenCollapsed.total <= limit ? t("Show all") : t("+{numOfQualitiesToHide} more", {
    numOfQualitiesToHide: numOfQualitiesToHide == null ? void 0 : numOfQualitiesToHide.toString()
  });
  return /* @__PURE__ */ jsx(Box, { padding: "xxsmall", paddingLeft: { mobile: "none" }, children: /* @__PURE__ */ jsx(Text, { size: "small", children: /* @__PURE__ */ jsx(
    TextLinkButton,
    {
      weight: "weak",
      onClick,
      icon: /* @__PURE__ */ jsx(IconChevron, { direction: "down" }),
      iconPosition: "trailing",
      children: showMoreText
    }
  ) }) });
};
const useQualitiesTitle = ({
  hasMatchedQualities,
  totalMatchedQualities,
  isInferredQualitiesTop
}) => {
  const { t } = useTranslations(translations$c);
  const { t: tCommon } = useTranslations(translations$6);
  if (hasMatchedQualities) {
    return t(
      "totalMatchedQualities skills and credentials match your profile",
      { totalMatchedQualities }
    );
  } else if (isInferredQualitiesTop) {
    return tCommon("Matches based on your career history");
  }
  return t("Skills and credentials from the job description");
};
const QualitiesSection = ({
  hasMatchedQualities,
  totalMatchedQualities,
  isInferredQualitiesTop,
  hasInferredQualities
}) => {
  const { isExpanded, setIsExpanded } = useSkillsMatchSignedInContext();
  const { matched } = useQualities();
  const qualitiesTitle = useQualitiesTitle({
    hasMatchedQualities,
    totalMatchedQualities,
    isInferredQualitiesTop
  });
  return /* @__PURE__ */ jsxs(Stack, { space: "xsmall", children: [
    /* @__PURE__ */ jsx(Text, { size: "small", children: qualitiesTitle }),
    /* @__PURE__ */ jsxs(Inline, { space: { mobile: "xxsmall", tablet: "xxsmall" }, alignY: "center", children: [
      hasMatchedQualities ? /* @__PURE__ */ jsx(UnselectableQualities, { qualities: matched }) : /* @__PURE__ */ jsx(
        SelectableQualities,
        {
          type: hasInferredQualities ? "inferred" : "unmatched"
        }
      ),
      !isExpanded && /* @__PURE__ */ jsx(
        ShowMoreButton,
        {
          onClick: () => {
            setIsExpanded(true);
          }
        }
      )
    ] })
  ] });
};
const TagTypeToBackgroundMap = { UrgentJobDisplayTag: "promoteLight" };
const getDisplayTags = (tags) => tags == null ? void 0 : tags.filter(({ __typename }) => __typename in TagTypeToBackgroundMap).map(({ __typename, label }) => ({
  background: TagTypeToBackgroundMap[__typename],
  label
}));
const match = "Match";
const missingSalaryPreference = "Missing Salary Preference";
const getIsSalaryMatch = (salaryMatchLabel) => salaryMatchLabel === match;
const getIsCandidateSetSalary = (salaryMatchLabel) => salaryMatchLabel !== missingSalaryPreference;
const isJobProfileSalaryMatch = (candidateSalaryMatch) => candidateSalaryMatch.salaryPreference !== void 0;
const getJobProfileSalaryMatch = (candidateSalaryMatch) => {
  if (candidateSalaryMatch && isJobProfileSalaryMatch(candidateSalaryMatch)) {
    const salaryMatchPreference = candidateSalaryMatch.salaryPreference;
    return {
      salaryCountryName: salaryMatchPreference.country.name,
      salaryExpectation: `${salaryMatchPreference.currencyCode} ${salaryMatchPreference.amount} ${salaryMatchPreference.salaryType}`
    };
  }
};
const useSalaryMatch = (candidateSalaryMatch) => {
  var _a, _b;
  const isSalaryMatch = getIsSalaryMatch(candidateSalaryMatch == null ? void 0 : candidateSalaryMatch.label);
  const isCandidateSetSalary = getIsCandidateSetSalary(
    candidateSalaryMatch == null ? void 0 : candidateSalaryMatch.label
  );
  const salaryMatchCountryName = (_a = getJobProfileSalaryMatch(candidateSalaryMatch)) == null ? void 0 : _a.salaryCountryName;
  const salaryExpectation = (_b = getJobProfileSalaryMatch(candidateSalaryMatch)) == null ? void 0 : _b.salaryExpectation;
  return {
    isSalaryMatch,
    isCandidateSetSalary,
    salaryExpectation,
    salaryMatchCountryName
  };
};
const brandToBrandNameMap = {
  seek: "SEEK",
  jobsdb: "Jobsdb",
  jobstreet: "Jobstreet"
};
const getBrandName = (brand) => brandToBrandNameMap[brand] || "SEEK";
const useBrandName = () => {
  const { brand } = useMelwaysInfo();
  return getBrandName(brand);
};
const useJobDetailsProps = ({
  jobDetails,
  jobApplyUrl,
  flags,
  topApplicant,
  salaryMatch
}) => {
  var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k, _l, _m, _n;
  const {
    job,
    sourcr,
    companyProfile,
    companySearchUrl,
    companyTags,
    restrictedApplication
  } = jobDetails;
  const {
    advertiser,
    classifications,
    contactMatches,
    content,
    id,
    isExpired,
    isLinkOut,
    isVerified,
    listedAt,
    location,
    products,
    salary,
    title,
    tracking,
    workTypes,
    phoneNumber,
    shareLink
  } = job;
  const {
    branding,
    questionnaire,
    video,
    displayTags: _displayTags
  } = products || {};
  const displayTags = getDisplayTags(_displayTags);
  const getProfileUrl = (companyNameSlug) => companyNameSlug ? `/companies/${companyNameSlug}` : void 0;
  const getReviewsUrl = (companyNameSlug, jobId) => companyNameSlug && jobId ? `/companies/${companyNameSlug}/reviews?jobId=${jobId}` : void 0;
  const {
    isSalaryMatch,
    isCandidateSetSalary,
    salaryExpectation,
    salaryMatchCountryName
  } = useSalaryMatch(salaryMatch);
  const { brand } = useMelwaysInfo();
  return {
    job: {
      id,
      appliedDateFormatted: flags.appliedDateFormatted,
      applyUrl: jobApplyUrl,
      shareLink,
      branding: branding ? {
        assets: {
          ...((_a = branding == null ? void 0 : branding.cover) == null ? void 0 : _a.url) && {
            cover: {
              id: "",
              strategies: {
                jdpCover: (_b = branding == null ? void 0 : branding.cover) == null ? void 0 : _b.url
              },
              url: (_c = branding == null ? void 0 : branding.cover) == null ? void 0 : _c.url
            }
          },
          logo: {
            id: "",
            strategies: {
              jdpLogo: (_d = branding == null ? void 0 : branding.logo) == null ? void 0 : _d.url
            },
            url: (_e = branding == null ? void 0 : branding.logo) == null ? void 0 : _e.url
          }
        }
      } : void 0,
      classifications: classifications.map(
        (classification) => classification.label
      ),
      company: {
        name: advertiser == null ? void 0 : advertiser.name,
        jobsSearchUrl: companySearchUrl,
        ...companyProfile && {
          profileUrl: getProfileUrl(companyProfile.companyNameSlug),
          ...((_f = companyProfile.reviewsSummary) == null ? void 0 : _f.overallRating) && {
            reviews: {
              count: companyProfile.reviewsSummary.overallRating.numberOfReviews.value,
              rating: companyProfile.reviewsSummary.overallRating.value,
              reviewsUrl: getReviewsUrl(companyProfile.companyNameSlug, id)
            }
          }
        },
        ...companyTags && {
          tags: companyTags
        },
        isVerified: (advertiser == null ? void 0 : advertiser.isVerified) || false,
        registrationDate: (_g = advertiser == null ? void 0 : advertiser.registrationDate) == null ? void 0 : _g.dateTimeUtc,
        verifiedBy: brandToBrandNameMap[brand] ?? "SEEK"
      },
      companyProfile: companyProfile ? {
        logo: ((_h = companyProfile.branding) == null ? void 0 : _h.logo) ?? void 0,
        profileUrl: getProfileUrl(companyProfile.companyNameSlug),
        reviewsUrl: getReviewsUrl(companyProfile.companyNameSlug, id),
        id: companyProfile.id,
        name: companyProfile.name,
        overview: {
          industry: ((_i = companyProfile.overview) == null ? void 0 : _i.industry) ?? void 0,
          companySize: (_k = (_j = companyProfile.overview) == null ? void 0 : _j.size) == null ? void 0 : _k.description,
          companyDescription: (_m = (_l = companyProfile.overview) == null ? void 0 : _l.description) == null ? void 0 : _m.paragraphs
        },
        perksAndBenefits: companyProfile.perksAndBenefits.map(
          (perk) => perk.title
        ),
        ...((_n = companyProfile.reviewsSummary) == null ? void 0 : _n.overallRating) && {
          overallRating: {
            rating: companyProfile.reviewsSummary.overallRating.value,
            reviewCount: companyProfile.reviewsSummary.overallRating.numberOfReviews.value
          }
        },
        shouldDisplayReviews: companyProfile.shouldDisplayReviews
      } : void 0,
      contactMatches,
      currencyLabel: salary == null ? void 0 : salary.currencyLabel,
      description: content,
      displayTags,
      employerQuestions: (questionnaire == null ? void 0 : questionnaire.questions) ?? [],
      isExpired,
      isLinkOut,
      isSaved: Boolean(flags == null ? void 0 : flags.isSaved),
      isVerified,
      isPrivateAdvertiser: (tracking == null ? void 0 : tracking.isPrivateAdvertiser) ?? false,
      location: location == null ? void 0 : location.label,
      postedDateFormatted: listedAt == null ? void 0 : listedAt.label,
      restrictedApplication,
      salary: salary == null ? void 0 : salary.label,
      isSignedIn: Boolean(flags == null ? void 0 : flags.isAuthenticated),
      isSalaryMatch,
      isCandidateSetSalary,
      salaryExpectation,
      salaryMatchCountryName,
      sourcr: sourcr ? {
        imageSrc: sourcr.image,
        imageMobileSrc: sourcr.imageMobile,
        profileUri: sourcr.link
      } : void 0,
      title,
      topApplicant,
      video: video && {
        link: video == null ? void 0 : video.url,
        position: video == null ? void 0 : video.position
      },
      workType: workTypes == null ? void 0 : workTypes.label,
      phoneNumber
    }
  };
};
const ToolTip = () => {
  const brand = useBrandName();
  const { t } = useTranslations(translations$d);
  return /* @__PURE__ */ jsx(
    TooltipRenderer,
    {
      id: "tooltop-how-you-match",
      tooltip: /* @__PURE__ */ jsx(Text, { children: t(
        "{brand} infers and matches skills and credentials based on the ad, your profile and default resume.",
        {
          brand
        }
      ) }),
      children: ({ triggerProps }) => /* @__PURE__ */ jsx(Box, { "aria-label": "Help", ...triggerProps, children: /* @__PURE__ */ jsx(Text, { children: /* @__PURE__ */ jsx(IconInfo, {}) }) })
    }
  );
};
const useAlertShownMessage = (type) => {
  const { t } = useTranslations(translations$5);
  switch (type) {
    case "resumeError":
      return t("Upload a current résumé to your profile to see better matches");
    case "skillLimit":
      return t("You cant have more than 50 skills");
  }
};
const getUseAlertTracking = (type) => {
  const alertShownTone = type === "resumeError" ? "info" : "critical";
  return ({ alertTriggered }) => {
    const alertShownMessage = useAlertShownMessage(type);
    const { onAnalyticsEvent } = useJobDetailsViewContext();
    const analytics = useAnalyticsHandlers(onAnalyticsEvent);
    const trackEvent = useCallback(() => {
      onSkillsMatchAlert({
        analytics,
        alertShownTone,
        alertShownMessage
      });
    }, [alertShownMessage, analytics]);
    const [isTrackingSent, setIsTrackingSent] = useState(false);
    useEffect(() => {
      if (alertTriggered && !isTrackingSent) {
        trackEvent();
        setIsTrackingSent(true);
      }
    }, [alertTriggered, isTrackingSent, trackEvent, setIsTrackingSent]);
  };
};
const useResumeAlertTracking = getUseAlertTracking("resumeError");
const useSkillsLimitAlertTracking = getUseAlertTracking("skillLimit");
const SkillsMatchSignedIn = ({
  isLoadingQualities = false,
  onAddQuality,
  onRemoveQuality,
  hasResume,
  error
}) => {
  const { matched, inferred, unmatched } = useQualities();
  if (isLoadingQualities) {
    return /* @__PURE__ */ jsx(SkillsMatchSkeleton, {});
  }
  if (!error && !matched.length && !inferred.length && !unmatched.length) {
    return null;
  }
  return /* @__PURE__ */ jsx(
    SkillsMatchContextProvider,
    {
      onAddQuality,
      onRemoveQuality,
      children: /* @__PURE__ */ jsx(SkillsMatch, { hasResume, error })
    }
  );
};
const SkillsMatch = ({ hasResume = true, error }) => {
  const { qualitiesWhenCollapsed } = useSkillsMatchSignedInContext();
  const { matched, inferred, unmatched } = useQualities();
  const { isSkillsLimitErrorShown, isExpanded, setIsExpanded } = useSkillsMatchSignedInContext();
  const { t } = useTranslations(translations$6);
  const hasMatchedQualities = matched.length > 0;
  const hasInferredQualities = inferred.length > 0;
  const hasUnmatchedQualities = unmatched.length > 0;
  const shouldShowOtherQualities = isExpanded && Number(hasMatchedQualities) + Number(hasInferredQualities) + Number(hasUnmatchedQualities) > 1;
  useResumeAlertTracking({
    alertTriggered: !hasResume && isExpanded
  });
  useSkillsLimitAlertTracking({
    alertTriggered: isSkillsLimitErrorShown && isExpanded
  });
  return /* @__PURE__ */ jsx(
    Box,
    {
      background: "body",
      boxShadow: "borderNeutralLight",
      borderRadius: "large",
      padding: { mobile: "medium", tablet: "large" },
      children: /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
        /* @__PURE__ */ jsxs(Inline, { space: "xxsmall", alignY: "center", children: [
          /* @__PURE__ */ jsx(Text, { weight: "strong", children: t("How you match") }),
          /* @__PURE__ */ jsx(ToolTip, {})
        ] }),
        error ? /* @__PURE__ */ jsx(ErrorMessage, {}) : /* @__PURE__ */ jsxs(Stack, { space: "large", children: [
          /* @__PURE__ */ jsx(
            QualitiesSection,
            {
              hasMatchedQualities,
              totalMatchedQualities: matched.length,
              isInferredQualitiesTop: qualitiesWhenCollapsed.type === "inferred",
              hasInferredQualities
            }
          ),
          isExpanded && /* @__PURE__ */ jsx(
            ExpandedContent,
            {
              shouldShowOtherQualities,
              inferred,
              unmatched,
              isInferredQualitiesTop: qualitiesWhenCollapsed.type === "inferred",
              isUnmatchedQualitiesTop: qualitiesWhenCollapsed.type === "unmatched",
              isExpanded,
              isSkillsLimitErrorShown,
              hasResume,
              hideAllOnClick: () => setIsExpanded(false)
            }
          )
        ] })
      ] })
    }
  );
};
const LOGO_HEIGHT_AND_WIDTH = 96;
const SkillsMatchSignedOutSvg = () => /* @__PURE__ */ jsxs(
  "svg",
  {
    width: LOGO_HEIGHT_AND_WIDTH,
    height: LOGO_HEIGHT_AND_WIDTH,
    viewBox: "0 0 116 116",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    children: [
      /* @__PURE__ */ jsx("circle", { cx: "58", cy: "58", r: "58", fill: "#fff" }),
      /* @__PURE__ */ jsx(
        "path",
        {
          d: "M80.15 36.484H63.11v8.394h-2.868c-2.162 0-3.951 1.687-3.99 3.76a3.807 3.807 0 0 0 1.098 2.758 3.805 3.805 0 0 0 2.735 1.148h3.026v8.393h6.553V59.78c0-3.159 2.498-5.774 5.568-5.83a5.579 5.579 0 0 1 4.08 1.624 5.631 5.631 0 0 1 1.698 4.048v1.316h6.553V43.931c0-4.106-3.325-7.447-7.413-7.447Z",
          fill: "#28BFCE"
        }
      ),
      /* @__PURE__ */ jsx(
        "path",
        {
          d: "M79.17 60.005a3.805 3.805 0 0 0-1.147-2.735 3.788 3.788 0 0 0-2.757-1.097c-2.074.038-3.761 1.828-3.761 3.99v2.998h-8.39v6.608h1.32a5.631 5.631 0 0 1 5.369 3.882 5.451 5.451 0 0 1 .167.57 5.683 5.683 0 0 1 .124.95c.003.056.01.11.012.165 0 .036-.004.07-.004.106 0 .036.005.07.004.106-.001.055-.01.11-.012.164a5.712 5.712 0 0 1-.023.325 5.691 5.691 0 0 1-.102.626l-.002.012c-.044.19-.1.375-.164.558a5.631 5.631 0 0 1-5.37 3.883h-1.319v6.498h17.003c4.175 0 7.446-3.27 7.446-7.446V63.16h-8.393v-3.156Z",
          fill: "#6E56E6"
        }
      ),
      /* @__PURE__ */ jsx(
        "path",
        {
          d: "m67.835 75.966.015-.117c.008-.074.012-.148.016-.222.002-.06.005-.121.005-.182s-.003-.122-.005-.182a3.685 3.685 0 0 0-.016-.222c-.004-.04-.01-.079-.015-.118-.267-1.862-1.948-3.311-3.951-3.311h-3.002v-8.448l-8.646.006v3.363a3.6 3.6 0 0 1-1.086 2.589 3.607 3.607 0 0 1-2.609 1.038c-1.963-.036-3.559-1.733-3.559-3.784v-3.2l-8.585.006.032 16.988c0 4.037 3.41 7.447 7.446 7.447h17.007v-8.34h3.002c2.003 0 3.684-1.449 3.95-3.31Z",
          fill: "#0D3880"
        }
      ),
      /* @__PURE__ */ jsx(
        "path",
        {
          d: "M37.852 57.688c.04.005.078.011.118.015.073.008.147.012.221.016.061.003.121.006.182.006s.122-.003.182-.006c.075-.004.149-.008.222-.016.04-.004.079-.01.118-.015 1.862-.267 3.311-1.948 3.311-3.95v-3.003h8.448l-.006-8.645h-3.363a3.6 3.6 0 0 1-2.588-1.086 3.607 3.607 0 0 1-1.039-2.61c.036-1.962 1.734-3.558 3.785-3.558h3.2l-.007-8.586-16.988.032c-4.037 0-7.447 3.41-7.447 7.447v17.006h8.34v3.002c0 2.003 1.449 3.684 3.311 3.951Z",
          fill: "#EE399E"
        }
      ),
      /* @__PURE__ */ jsx(
        "path",
        {
          d: "M63.389 29.695h-3.707a1.09 1.09 0 1 1 0-2.18h3.707a1.09 1.09 0 1 1 0 2.18ZM51.59 21.605a1.09 1.09 0 0 1-1.09-1.09V16.81a1.09 1.09 0 1 1 2.18 0v3.707a1.09 1.09 0 0 1-1.09 1.09ZM57.31 23.977a1.09 1.09 0 0 1-.77-1.86l2.621-2.622a1.09 1.09 0 1 1 1.541 1.541l-2.62 2.621c-.214.213-.493.32-.772.32Z",
          fill: "#FDC221"
        }
      )
    ]
  }
);
const currentPage = "job details";
const currentPageSection = "skills match";
const SkillsMatchSignedOut = ({
  registerUrl,
  signInUrl
}) => {
  const { t } = useTranslations(translations$9);
  const { onAnalyticsEvent } = useJobDetailsViewContext();
  const analytics = useAnalyticsHandlers(onAnalyticsEvent);
  return /* @__PURE__ */ jsx(
    Box,
    {
      background: "neutralSoft",
      borderRadius: "xlarge",
      padding: { mobile: "medium", tablet: "large" },
      children: /* @__PURE__ */ jsxs(Columns, { space: "none", alignY: "center", children: [
        /* @__PURE__ */ jsx(Column, { children: /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
          /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
            /* @__PURE__ */ jsx(Heading, { component: "h2", level: "4", children: t("How do your skills match this job?") }),
            /* @__PURE__ */ jsx(Text, { size: "standard", children: t("Sign in and update your profile to get insights.") })
          ] }),
          /* @__PURE__ */ jsxs(Inline, { space: "small", children: [
            /* @__PURE__ */ jsx(
              ButtonLink,
              {
                href: registerUrl,
                onClick: () => analytics.onSkillsMatchRegisterPressed({
                  currentPage,
                  currentPageSection
                }),
                rel: "noopener",
                role: "button",
                tone: "neutral",
                variant: "ghost",
                size: "small",
                children: t("Register")
              }
            ),
            /* @__PURE__ */ jsx(
              ButtonLink,
              {
                href: signInUrl,
                onClick: () => analytics.onSkillsMatchSignInPressed({
                  currentPage,
                  currentPageSection
                }),
                rel: "noopener",
                role: "button",
                tone: "neutral",
                variant: "transparent",
                size: "small",
                children: t("Sign In")
              }
            )
          ] })
        ] }) }),
        /* @__PURE__ */ jsx(Column, { width: "content", hideBelow: "tablet", children: /* @__PURE__ */ jsx(SkillsMatchSignedOutSvg, {}) })
      ] })
    }
  );
};
const JobDetailsViewBase = () => {
  var _a, _b, _c, _d;
  const {
    careerInsights,
    ctas,
    embedded,
    hideJobLink,
    hideOpenInNewTab,
    isPDPAConsentRequired,
    job,
    jobUrlOpts,
    onAnalyticsEvent,
    renderRelativePaths,
    reportJobAdInitialValues,
    featureFlags,
    displayCompanyProfile,
    registerUrl,
    signInUrl,
    isLoggedIn,
    qualities,
    competitivePlacement
  } = useJobDetailsViewContext();
  const { shouldShowSkillsMatch = true } = qualities ?? {};
  const { t } = useTranslations(translations$1);
  const analytics = useAnalyticsHandlers(onAnalyticsEvent);
  const { ref: trackedComponent } = useInView({
    onChange(inView) {
      if (inView) {
        analytics.onSourcrImpression();
      }
    },
    triggerOnce: true
  });
  const video = job.video ? /* @__PURE__ */ jsx(Hidden, { print: true, children: /* @__PURE__ */ jsx(ResponsiveEmbed, { url: job.video.link }) }) : null;
  const restrictedApplicationNotice = job.restrictedApplication.label ? /* @__PURE__ */ jsx(Alert, { tone: "caution", children: /* @__PURE__ */ jsx(Text, { children: job.restrictedApplication.label }) }) : void 0;
  const showCompanyProfile = displayCompanyProfile && job.companyProfile;
  const showCompanyInfo = job.company.tags && job.company.tags.length > 0;
  const showCompetitiveJobs = (competitivePlacement == null ? void 0 : competitivePlacement.isLoadingCompetitiveJobs) || ((_a = competitivePlacement == null ? void 0 : competitivePlacement.jobs) == null ? void 0 : _a.length);
  const companyInformation = /* @__PURE__ */ jsx(Box, { component: "section", children: /* @__PURE__ */ jsxs(Stack, { space: "large", children: [
    /* @__PURE__ */ jsx(Heading, { component: "h2", level: embedded ? "4" : "3", children: t("Company information") }),
    /* @__PURE__ */ jsx(
      Tiles,
      {
        space: "large",
        columns: {
          mobile: 1,
          tablet: 2
        },
        children: (_b = job.company.tags) == null ? void 0 : _b.map(({ key, value }) => /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
          /* @__PURE__ */ jsx(Text, { weight: "medium", children: key }),
          /* @__PURE__ */ jsx(Text, { children: value })
        ] }, `${key}`))
      }
    )
  ] }) });
  const hasBadge = useHasBadge();
  const badgeAndDisplayTags = hasBadge || job.displayTags ? /* @__PURE__ */ jsxs(Inline, { space: "xsmall", children: [
    hasBadge ? /* @__PURE__ */ jsx(JobStatusBadge, {}) : null,
    job.displayTags ? /* @__PURE__ */ jsx(JobDisplayTags, { displayTags: job.displayTags }) : null
  ] }) : null;
  const trackVerifiedHirerBadgeClick = (ctas == null ? void 0 : ctas.trackVerifiedHirerBadgeClick) ?? (() => {
  });
  const trackMoreAboutThisCompanyClick = (ctas == null ? void 0 : ctas.trackMoreAboutThisCompanyClick) ?? (() => {
  });
  const isApplyDisabled = useIsApplyDisabled();
  const isMatchedQualitiesEnabled = featureFlags == null ? void 0 : featureFlags.enableMatchedQualities;
  return /* @__PURE__ */ jsx(
    JDVLayout,
    {
      isExpired: job.isExpired,
      onAnalyticsEvent,
      jobUrl: !hideJobLink ? job.url : void 0,
      hideJobLink,
      jobTitle: job.title,
      id: job.id,
      jobUrlOpts,
      renderRelativePaths,
      companyName: job.company.name,
      embedded,
      stickApplyButtonOnDesktop: featureFlags == null ? void 0 : featureFlags.stickApplyButtonOnDesktop,
      applyAction: /* @__PURE__ */ jsx(ApplySection, {}),
      saveAction: /* @__PURE__ */ jsx(
        SaveButton,
        {
          job,
          ctas,
          mobileOptions: isApplyDisabled ? {
            displayText: true,
            // should show button text in mobile view when Apply is disabled
            displayIcon: false
            // should not display icon when Apply is disabled
          } : void 0
        }
      ),
      phoneNumber: job.phoneNumber,
      hideApplyButtonForPrivateAds: isApplyDisabled,
      restrictedApplicationNotice,
      branding: job.branding,
      badgeAndDisplayTags,
      header: /* @__PURE__ */ jsx(
        JobDetailsViewHeader,
        {
          trackVerifiedHirerBadgeClick,
          trackMoreAboutThisCompanyClick
        }
      ),
      headerAdditional: /* @__PURE__ */ jsx(JobDetailsViewHeaderAdditional, {}),
      mainContent: /* @__PURE__ */ jsxs(Fragment, { children: [
        isMatchedQualitiesEnabled && isLoggedIn && shouldShowSkillsMatch && /* @__PURE__ */ jsx(SkillsMatchSignedIn, { ...qualities }),
        /* @__PURE__ */ jsx("section", { children: /* @__PURE__ */ jsxs(Stack, { space: "large", children: [
          ((_c = job.video) == null ? void 0 : _c.position) === "TOP" && video,
          Boolean(job.description) && /* @__PURE__ */ jsx(
            JobDescription,
            {
              description: job.description,
              contactMatches: job.contactMatches,
              onContactMatchImpression: analytics.onContactMatchImpression,
              onContactMatchInteraction: analytics.onContactMatchInteraction
            }
          ),
          job.phoneNumber ? /* @__PURE__ */ jsx(Text, { children: t("For more information, contact us on {phoneNumber}.", {
            phoneNumber: job.phoneNumber,
            TextLink: (children) => /* @__PURE__ */ jsx(TextLink, { href: `tel:${children}`, weight: "weak", children })
          }) }) : void 0,
          ((_d = job.video) == null ? void 0 : _d.position) === "BOTTOM" && video
        ] }) }),
        isMatchedQualitiesEnabled && !isLoggedIn && /* @__PURE__ */ jsx(
          SkillsMatchSignedOut,
          {
            registerUrl,
            signInUrl
          }
        ),
        showCompanyInfo && !showCompanyProfile && /* @__PURE__ */ jsx(Fragment, { children: companyInformation }),
        job.sourcr && /* @__PURE__ */ jsx("section", { ref: trackedComponent, children: /* @__PURE__ */ jsxs(Stack, { space: "large", children: [
          /* @__PURE__ */ jsx(Heading, { component: "h2", level: embedded ? "4" : "3", children: t("About the recruiter") }),
          /* @__PURE__ */ jsx(
            Box,
            {
              boxShadow: "borderNeutralLight",
              borderRadius: "large",
              paddingX: ["small", "gutter", "large"],
              paddingY: ["gutter", "gutter", "large"],
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              children: /* @__PURE__ */ jsx(
                "a",
                {
                  href: job.sourcr.profileUri,
                  onClick: analytics.onSourcrClicked,
                  "data-testid": "sourcr-profile",
                  rel: "noopener noreferrer",
                  target: "_blank",
                  children: /* @__PURE__ */ jsxs("picture", { children: [
                    /* @__PURE__ */ jsx(
                      "source",
                      {
                        media: `(max-width: ${breakpoints.tablet - 1}px)`,
                        srcSet: job.sourcr.imageMobileSrc
                      }
                    ),
                    /* @__PURE__ */ jsx(
                      "source",
                      {
                        media: `(min-width: ${breakpoints.tablet}px)`,
                        srcSet: job.sourcr.imageSrc
                      }
                    ),
                    /* @__PURE__ */ jsx("img", { className: sourcrImage, loading: "lazy" })
                  ] })
                }
              )
            }
          )
        ] }) }),
        Boolean(job.employerQuestions.length) && /* @__PURE__ */ jsx("section", { children: /* @__PURE__ */ jsxs(Stack, { space: "large", children: [
          /* @__PURE__ */ jsx(Heading, { component: "h2", level: embedded ? "4" : "3", children: t("Employer questions") }),
          /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
            /* @__PURE__ */ jsx(Text, { children: t(
              "Your application will include the following questions:"
            ) }),
            /* @__PURE__ */ jsx(List, { space: "small", children: job.employerQuestions.map((question, index) => /* @__PURE__ */ jsx(Text, { children: question }, `${question}_${index}`)) })
          ] })
        ] }) }),
        showCompanyProfile && job.companyProfile && /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx(Box, { component: "section", children: /* @__PURE__ */ jsx(
            CompanyProfile,
            {
              trackVerifiedHirerBadgeClick,
              trackMoreAboutThisCompanyClick,
              onAnalyticsEvent,
              ...job.companyProfile
            }
          ) }),
          showCompanyInfo && /* @__PURE__ */ jsx(Fragment, { children: companyInformation })
        ] }),
        (ctas == null ? void 0 : ctas.onReportJobAd) ? /* @__PURE__ */ jsx("section", { children: /* @__PURE__ */ jsx(
          ReportJobAd$1,
          {
            onSubmit: ctas.onReportJobAd,
            initialValues: reportJobAdInitialValues,
            isPDPAConsentRequired,
            isVerified: job.isVerified,
            renderRelativePaths
          }
        ) }) : null,
        showCompetitiveJobs ? /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx(Divider, {}),
          /* @__PURE__ */ jsx(
            CompetitiveJobs,
            {
              jobs: competitivePlacement == null ? void 0 : competitivePlacement.jobs,
              isLoadingCompetitiveJobs: competitivePlacement == null ? void 0 : competitivePlacement.isLoadingCompetitiveJobs
            }
          )
        ] }) : null,
        Boolean(careerInsights) ? /* @__PURE__ */ jsx("section", { children: /* @__PURE__ */ jsx(
          Box,
          {
            boxShadow: "borderNeutralLight",
            borderRadius: "large",
            padding: ["gutter", "gutter", "xlarge"],
            style: { margin: "auto" },
            children: /* @__PURE__ */ jsx(Box, { style: { maxWidth: "100%", margin: "auto" }, children: careerInsights })
          }
        ) }) : null
      ] }),
      menu: /* @__PURE__ */ jsx(OptionsBar, {}),
      hideOpenInNewTab
    }
  );
};
const JobDetailsView = (props) => /* @__PURE__ */ jsx(JobDetailsViewContextProvider, { ...props, children: /* @__PURE__ */ jsx(JobDetailsViewBase, {}) });
export {
  JobDetailsView,
  useJobDetailsProps,
  useSkillsMatchSignedInContext
};
